import { useTranslation } from "react-i18next";
import PageWrapper from "../../components/PageWrapper";
import useLanguage from "../../utils/useLanguage";
import { UserAuth } from "../../utils/AuthContext";
import { useContext, useEffect, useState } from "react";
import RoundedTable, {
  TABLE_CELL_TYPES,
} from "../../components/table-component/RoundedTable";
import CustomButton from "../../components/CustomButton";
import infoIcon from "../../assets/info.png";
import Modal from "../../components/Modal";
import ErrorBox from "../../components/ErrorBox";
import classes3 from "../../components/PDFUploader.module.css";
import deleteIcon from "../../assets/circled_cancel.svg";
import uploadIcon from "../../assets/upload_icon.png";
import closeIcon from "../../assets/close_icon.png";
import { DoneIcon, WarningIcon } from "../../assets/registtyIcons/icons";
import { useNavigate } from "react-router-dom";
import SectionDivider from "../../components/SectionDivider";
import CustomInput from "../../components/CustomInput";
import Loader, { LOADER_SIZES } from "../../components/Loader";
import saveIcon from "../../assets/save_icon.png";
import addIcon from "../../assets/add_icon_light.png";
import { useInvoices } from "./Invoices";
import InformationBox from "../../components/InformationBox";

const AIOrganizationInvoicesPage = () => {
  const { t } = useTranslation("general");
  const { currentDirection } = useLanguage();
  const { userData } = useContext(UserAuth);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
  });
  const { invoices, setInvoices } = useInvoices();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);

  const tableTitles = [
    {
      keepOnMobile: true,
      value: "transactionNumber",
    },
    {
      keepOnMobile: true,
      value: "Actions",
    },
  ];

  const fetchInvoices = async () => {
    setLoading(true);
    try {
      await new Promise((resolve) => setTimeout(resolve, 1000));
      setLoading(false);
    } catch (error) {
      console.log("error fetching invoices", error);
      setError("errorFetchingInvoices");
    } finally {
      setLoading(false);
    }
  };

  const tableRows = invoices?.map((invoice) => [
    {
      value: invoice.invoiceNumber,
      type: TABLE_CELL_TYPES.LINK,
      to: `/ai-organization-dashboard/invoices/${invoice.id}`,
      eclipseText: true,
      keepOnMobile: true,
    },
    {
      value: (
        <CustomButton
          title={t("info")}
          icon={infoIcon}
          iconSize={16}
          variant="blank"
        />
      ),
      type: TABLE_CELL_TYPES.ELEMENT,
      keepOnMobile: true,
    },
  ]);

  useEffect(() => {
    fetchInvoices();
  }, [pagination.currentPage]);

  const handleSave = (data) => {
    setInvoices((prev) => [data, ...prev]);
    setShowUploadModal(false);
  };

  return (
    <PageWrapper
      style={{ direction: currentDirection }}
      title={t("transactionsPage")}
      withBackButton
    >
      <div className="o-row o-row--horizontal-align_end">
        <CustomButton
          title={t("createTransaction")}
          onClick={() => setShowUploadModal(true)}
          icon={addIcon}
        />
      </div>
      <div className="o-column o-column--gap-size_500 u-margin--top_500">
        <section>
          {loading && <Loader size={LOADER_SIZES.MEDIUM} />}
          {error && <ErrorBox>{t(error)}</ErrorBox>}
          {!error && !loading && invoices?.length === 0 && (
            <InformationBox>{t("noTransactions2")}</InformationBox>
          )}
          {!loading && !error && invoices?.length > 0 && (
            <RoundedTable
              titles={tableTitles}
              rows={tableRows}
              pagination={pagination}
              setPagination={setPagination}
            />
          )}
        </section>
      </div>

      {showUploadModal && (
        <UploadApplicationsDocumentsModal
          onClose={() => setShowUploadModal(false)}
          onSave={handleSave}
        />
      )}
    </PageWrapper>
  );
};

function UploadApplicationsDocumentsModal({
  onClose,
  onSave,
  modalSize = "medium",
}) {
  const { t } = useTranslation("general");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [files, setFiles] = useState({
    invoice: null,
    bankForm: null,
    iraqiCompanyCertificate: null,
    supplierCertificate: null,
  });

  const [invoiceNumber, setInvoiceNumber] = useState("");

  const handleClose = () => {
    setFiles({
      invoice: null,
      bankForm: null,
      iraqiCompanyCertificate: null,
      supplierCertificate: null,
    });
    setError(null);
    onClose();
  };

  const handleFileChange = (fieldName) => (event) => {
    const file = event.target.files[0];
    setFiles((prev) => ({
      ...prev,
      [fieldName]: file,
    }));

    if (error) setError(null);
  };

  const handleDeleteFile = (fieldName) => {
    setFiles((prev) => ({
      ...prev,
      [fieldName]: null,
    }));

    const fileInput = document.getElementById(fieldName);
    if (fileInput) {
      fileInput.value = "";
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      setError(null);

      if (!invoiceNumber) {
        setError("invoiceNumberRequired");
        setLoading(false);
        return;
      }
      const formData = new FormData();

      Object.keys(files).forEach((fieldName) => {
        if (files[fieldName]) {
          formData.append(fieldName, files[fieldName]);
        }
      });

      const response = await new Promise((r) => {
        setTimeout(() => {
          r({
            status: 200,
            data: {
              invoiceNumber,
              files,
            },
          });
        }, 2000);
      });

      handleClose();
      onSave?.({
        id: Math.random().toString(36).substring(2, 9),
        invoiceNumber,
        files: {
          invoice: files.invoice,
          bankForm: files.bankForm,
          supplierCertificate: files.supplierCertificate,
          articlesOfAssociation: files.iraqiCompanyCertificate,
        },
      });
    } catch (err) {
      console.log(err);
      setError("ErrorSavingInvoice");
    } finally {
      setLoading(false);
    }
  };

  const renderFileUpload = (fieldName, labelKey) => (
    <div className="o-column u-margin--top_200">
      <SectionDivider title={t(labelKey)} noMargin={true} />

      <div className="o-column o-column--gap-size_100 u-padding_0">
        <div className="u-width--max-width_300">
          <input
            type="file"
            id={fieldName}
            accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
            onChange={handleFileChange(fieldName)}
            className={`${classes3.fileInput} u-padding_0`}
          />
          <div>
            <label htmlFor={fieldName}>
              <p className={` ${classes3.customFileInput}`}>
                <img src={uploadIcon} alt={`Upload image`} />
                <span>{t("uploadDocument")}</span>
              </p>
            </label>
          </div>

          <div className="u-width--max-width_300 o-row o-row--vertical-align_center">
            <div className="o-row o-row--vertical-align_center">
              {files[fieldName] ? (
                <span className="">
                  <DoneIcon width={30} height={30} />
                  {/* {t("fileSelected")} */}
                </span>
              ) : (
                <span className="u-color_success u-font-size_small o-row o-row--vertical-align_center o-row--gap-size_200">
                  <WarningIcon width={20} height={20} />
                  {t("noFileSelected")}
                </span>
              )}
            </div>
            {files[fieldName] && (
              <div className={classes3.fileName}>
                {t("fileName")} : {files[fieldName]?.name?.slice(0, 30)}
                <button
                  className={classes3.deleteFileButton}
                  onClick={() => handleDeleteFile(fieldName)}
                >
                  <img src={deleteIcon} className="u-height-specific_20" />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <Modal
      title={t("uploadTransactionDocuments")}
      onClose={handleClose}
      modalSize={modalSize}
      error={t(error)}
      footer={
        <>
          {loading && <Loader size={LOADER_SIZES.MEDIUM} />}
          <section className="o-row o-row--gap-size_300 o-row--horizontal-align_end">
            <CustomButton
              title={t("cancel")}
              onClick={handleClose}
              variant="blank"
              icon={closeIcon}
              iconSize={14}
            />
            <CustomButton
              title={t("save")}
              onClick={handleSave}
              icon={saveIcon}
              iconSize={20}
              disabled={loading}
            />
          </section>
        </>
      }
    >
      <div className="o-column o-column--gap-size_300">
        {error && <ErrorBox>{t(error)}</ErrorBox>}
        <CustomInput
          label={t("invoiceNumber")}
          value={invoiceNumber}
          onChange={(e) => setInvoiceNumber(e.target.value)}
          title={t("invoiceNumber")}
          errorText={
            error === "invoiceNumberRequired"
              ? t("invoiceNumberRequired")
              : null
          }
        />

        <div className=" u-margin--top_500" />
        <SectionDivider title="sectionOne" noMargin={true} />
        <div className="o-column o-column--gap-size_200 u-margin--bottom_500">
          {renderFileUpload("invoice", "invoice")}
          {renderFileUpload("bankForm", "financialTransferRequestForm")}
          {renderFileUpload("supplierCertificate", "supplierCertificate")}
          {renderFileUpload("articlesOfAssociation", "articlesOfAssociation")}
        </div>

        <SectionDivider title="sectionTwo" noMargin={true} />
        <div className="o-column o-column--gap-size_200">
          {renderFileUpload("confirm", "confirmDocument")}
          {renderFileUpload("moneyTransactionRequest", "moneyTransactionRequest")}
          {renderFileUpload("declarationAndUndertaking", "declarationAndUndertaking")}
        </div>
      </div>
    </Modal>
  );
}

export default AIOrganizationInvoicesPage;
